// Multihit / rhythm patterns: a struck event can spawn a series of follow-up hits.
// Pure: returns time offsets (seconds, relative to the primary hit) + energy scales.
// The primary hit fires at t=0 separately; this returns only the EXTRA hits.
import type { Rng } from '@shared/rng';

export type MultihitPattern = 'none' | 'flam' | 'double' | 'triplet' | 'roll' | 'euclidean';

export interface MultihitConfig {
  pattern: MultihitPattern;
  /** number of follow-up hits (roll); pulses (euclidean). Default per-pattern. */
  count?: number;
  /** total time the pattern spans, seconds. Default 0.4. */
  spreadSec?: number;
  /** euclidean step count (M in E(K,M)). Default 8. */
  steps?: number;
}

export interface Hit {
  offsetSec: number;
  energyScale: number; // multiplied onto the base energy
}

/** Bjorklund-style Euclidean rhythm: K pulses spread as evenly as possible over M steps. */
export function euclid(pulses: number, steps: number): boolean[] {
  const k = Math.max(0, Math.min(pulses, steps));
  const out: boolean[] = [];
  let bucket = 0;
  for (let i = 0; i < steps; i++) {
    bucket += k;
    if (bucket >= steps) {
      bucket -= steps;
      out.push(true);
    } else {
      out.push(false);
    }
  }
  return out;
}

/** Extra hits for a multihit pattern (excludes the primary t=0 hit). */
export function multihitHits(cfg: MultihitConfig, rng: Rng): Hit[] {
  const spread = cfg.spreadSec ?? 0.4;
  switch (cfg.pattern) {
    case 'none':
      return [];
    case 'flam':
      // a single soft grace note just after the primary
      return [{ offsetSec: 0.02 + 0.02 * rng(), energyScale: 0.55 }];
    case 'double':
      return [{ offsetSec: spread * 0.5, energyScale: 0.85 }];
    case 'triplet':
      return [
        { offsetSec: spread / 3, energyScale: 0.8 },
        { offsetSec: (2 * spread) / 3, energyScale: 0.7 },
      ];
    case 'roll': {
      const n = Math.max(2, cfg.count ?? 5);
      const hits: Hit[] = [];
      for (let i = 1; i <= n; i++) {
        const t = (i / n) * spread;
        // gently accelerating feel + decaying energy
        hits.push({ offsetSec: t * (0.85 + 0.15 * (i / n)), energyScale: 0.85 - 0.5 * (i / n) });
      }
      return hits;
    }
    case 'euclidean': {
      const steps = cfg.steps ?? 8;
      const k = Math.max(1, cfg.count ?? 3);
      const pattern = euclid(k, steps);
      const hits: Hit[] = [];
      for (let i = 1; i < steps; i++) {
        // i=0 is the primary hit; later pulses become follow-ups
        if (pattern[i]) hits.push({ offsetSec: (i / steps) * spread, energyScale: 0.75 });
      }
      return hits;
    }
    default:
      return [];
  }
}
